// ===========================================
// APPLICATION CONFIGURATION
// ===========================================

import type { Language, Currency } from '@/types';

/**
 * Application configuration derived from environment variables
 */
export const appConfig = {
  // App Identity
  name: import.meta.env.VITE_APP_NAME || 'TunisTravels',
  url: import.meta.env.VITE_APP_URL || 'https://www.tunistravels.com',
  description: import.meta.env.VITE_APP_DESCRIPTION || 'Your trusted travel agency',

  // Localization
  defaultLanguage: (import.meta.env.VITE_DEFAULT_LANGUAGE || 'en') as Language,
  supportedLanguages: (import.meta.env.VITE_SUPPORTED_LANGUAGES || 'en,fr,ar,de,it,zh,es').split(',') as Language[],
  defaultCurrency: (import.meta.env.VITE_DEFAULT_CURRENCY || 'USD') as Currency,

  // Feature Flags
  features: {
    analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    booking: import.meta.env.VITE_ENABLE_BOOKING === 'true',
    reviews: import.meta.env.VITE_ENABLE_REVIEWS === 'true',
    newsletter: import.meta.env.VITE_ENABLE_NEWSLETTER === 'true',
  },

  // Social Media
  social: {
    facebook: import.meta.env.VITE_FACEBOOK_URL || '',
    instagram: import.meta.env.VITE_INSTAGRAM_URL || '',
    twitter: import.meta.env.VITE_TWITTER_URL || '',
    youtube: import.meta.env.VITE_YOUTUBE_URL || '',
  },

  // Contact Information (can be overridden per domain)
  contact: {
    email: 'info@tunistravels.com',
    phone: '+216 XX XXX XXX',
    whatsapp: '+216 XX XXX XXX',
    address: {
      street: '123 Avenue de la République',
      city: 'Tunis',
      country: 'Tunisia',
      postalCode: '1000',
    },
  },

  // Business Hours
  businessHours: {
    weekdays: '09:00 - 18:00',
    saturday: '09:00 - 13:00',
    sunday: 'Closed',
    timezone: 'Africa/Tunis',
  },
} as const;

/**
 * Check if a feature is enabled
 */
export const isFeatureEnabled = (feature: keyof typeof appConfig.features): boolean => {
  return appConfig.features[feature];
};

/**
 * Get supported language codes
 */
export const getSupportedLanguages = (): Language[] => {
  return appConfig.supportedLanguages;
};

/**
 * Check if a language is supported
 */
export const isLanguageSupported = (lang: string): lang is Language => {
  // Accept any valid 2-5 character language code (e.g., en, fr, zh, zh-CN)
  // The backend API controls which languages are actually available
  // This guard only prevents obviously invalid URL segments from being treated as languages
  return /^[a-z]{2,5}(-[a-zA-Z]{2,4})?$/.test(lang);
};

export type AppConfig = typeof appConfig;
